import { z } from 'zod';
import { GeoPointSchema } from './api/subjects.gen';
import type { DomainField } from './api/domains.gen';

// The DYNAMIC half of G-26, for the domain-driven Sites form (P03 §5, §7.1;
// story 3-1b).
//
// DOD §4 G-26 names two regimes for one primitive: a **static/CRUD** form takes
// its schema from `.gen.ts`, and the **dynamic** surface builds one at runtime.
// The Sites create/edit form is BOTH at once, and that is the whole design:
//
//     CreateSubjectBodySchema.extend({ attributes: attributesSchemaFor(fields) })
//
// The envelope (`domainKey`, `name`, `referenceCode`, …) comes from the
// generated contract — composition over the generated schema, exactly what
// G-26 permits ("`.extend()` / `.omit()` / `.pick()` / `.merge()`"). Only
// `attributes`, whose shape is DATA (a domain pack is a seed row, not code),
// is assembled here, from the very `subjectSchema.fields` the server serves via
// `GET /v1/domains`.
//
// ── Why this file is a twin, and how it is kept honest ────────────────────
// The identical rule exists as a JS zod factory in `contracts/subjects.js`
// (`attributesSchemaFor`), because the backend must apply it too and cannot
// import TypeScript. Two runtimes, one rule — so the rule is held to a single
// verdict by `scripts/subject-attributes-parity.test.mjs`, which transpiles
// THIS file, evaluates the shipped `subjects.gen.ts` schemas, and runs a table
// of inputs through both sides asserting they agree. That is the same
// technique `scripts/contract-parity.test.mjs` uses for the emitted client
// schemas: the artifact that ships is the artifact under test.
// Any change here that is not mirrored in `contracts/subjects.js` fails that
// gate loudly, rather than surfacing as a 400 the user cannot act on.

/**
 * Pack-field keys that are Subject COLUMNS, not attributes (P03 §2). The
 * seeded `building` pack declares `referenceCode` among its fields; it renders
 * as the top-level control and is written to the top-level body key, so one
 * string never lives in two places. Mirrors `RESERVED_FIELD_KEYS` in
 * `contracts/subjects.js` — the parity gate asserts the two lists are equal.
 */
export const RESERVED_FIELD_KEYS = ['name', 'referenceCode'];

// An empty numeric input arrives as `''` (a controlled `<input type="number">`
// always sends its shape), and `z.coerce.number()` turns `''` into 0 — so a
// REQUIRED number left blank would silently pass as a real answer of zero.
// Normalise blank to absent first, keep string→number coercion for a typed
// value, and leave anything non-numeric as-is so `z.number()` reports it.
// Mirrored in contracts/subjects.js.
function numericValue(value: unknown): unknown {
  if (value === '' || value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return value.trim() === '' || Number.isNaN(parsed) ? value : parsed;
  }
  return value;
}

/**
 * The subset of a field definition this rule reads (story 5-10b).
 *
 * `DomainField` satisfies it, and so does one slot of a pinned FormSchema v2
 * template — which is the point: the capture surface builds its runtime schema
 * from the SAME rule rather than a second switch of its own (G-26's dynamic
 * regime, P05 §9.5's "identical rules"). Widened from `DomainField` rather than
 * duplicated, so a change to what a `number` accepts still has one home.
 */
export interface FieldSchemaSpec {
  key: string;
  type: string;
  label: string;
  required?: boolean | undefined;
  min?: number | undefined;
  max?: number | undefined;
}

/**
 * The zod schema for ONE domain-pack field. `null` means the field renders no
 * control and holds no value (`instruction` — static text, FormSchema v2).
 */
export function fieldSchema(field: FieldSchemaSpec): z.ZodTypeAny | null {
  const required = Boolean(field.required);
  const label = field.label || field.key;
  switch (field.type) {
    case 'number':
      return required
        ? z.preprocess(
            numericValue,
            z.number({ required_error: `${label} is required`, invalid_type_error: `${label} must be a number` }),
          )
        : z.preprocess(numericValue, z.number().optional());
    case 'boolean':
      return required ? z.boolean() : z.boolean().optional();
    case 'select':
      return required ? z.string().min(1, `${label} is required`) : z.string().optional();
    case 'multiselect':
      return required ? z.array(z.string()).min(1, `${label} is required`) : z.array(z.string()).optional();
    case 'rating':
      return required ? z.number({ invalid_type_error: `${label} is required` }) : z.number().optional();
    case 'date':
    case 'datetime':
      return required ? z.string().min(1, `${label} is required`) : z.string().optional();
    case 'gps':
      // GeoPointSchema is the GENERATED contract schema — the same object the
      // backend validates a `gps` value with. Never re-declared here.
      if (required) return GeoPointSchema;
      // An OPTIONAL address the user never touched still arrives as an object
      // full of empty strings (a controlled control always sends its shape),
      // which a strict GeoPoint rejects — making an optional address field
      // impossible to leave blank. Normalise "nothing entered" to absent.
      // `country` is excluded: it carries a default, so its presence never
      // means the user typed one. Mirrored in contracts/subjects.js.
      return z.preprocess(
        (value) =>
          value &&
          typeof value === 'object' &&
          !['line1', 'line2', 'city', 'state', 'postcode'].some((key) =>
            String((value as Record<string, unknown>)[key] ?? '').trim(),
          )
            ? undefined
            : value,
        GeoPointSchema.optional(),
      );
    case 'instruction':
      return null;
    case 'text':
    case 'textarea':
    default:
      return required ? z.string().min(1, `${label} is required`) : z.string().optional();
  }
}

/**
 * The `attributes` schema for a domain pack, built from its
 * `subjectSchema.fields`. `.strict()` deliberately: sending a key the pack
 * does not declare is a client/server disagreement, and stripping it silently
 * would hide the drift this function exists to prevent.
 */
export function attributesSchemaFor(fields: DomainField[]): z.ZodTypeAny {
  const shape: Record<string, z.ZodTypeAny> = {};
  (fields || [])
    .filter((field) => field && field.key && RESERVED_FIELD_KEYS.indexOf(field.key) === -1)
    .forEach((field) => {
      const schema = fieldSchema(field);
      if (schema) shape[field.key] = schema;
    });
  return z.object(shape).strict();
}

/**
 * The value a field starts at in an empty form. RHF needs every controlled
 * input to start DEFINED (an `undefined` → string transition remounts the
 * control as uncontrolled and loses focus mid-typing), so each type gets the
 * empty value its DS control expects rather than a blanket `''`.
 */
export function fieldDefault(field: DomainField): unknown {
  switch (field.type) {
    case 'boolean':
      return false;
    case 'multiselect':
      return [];
    case 'rating':
      return undefined;
    case 'number':
      // `''`, not undefined: an `<input type="number">` must stay CONTROLLED
      // from the first render (an undefined→string transition remounts it as
      // uncontrolled and drops focus mid-typing). `numericValue` above turns
      // that blank back into "absent" at validation time.
      return '';
    case 'gps':
      return { line1: '', line2: '', city: '', state: '', postcode: '', country: 'Australia' };
    case 'instruction':
      return undefined;
    default:
      return '';
  }
}

/** Default `attributes` for a pack — the empty form's starting values. */
export function attributeDefaults(fields: DomainField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields || []) {
    if (!field || !field.key) continue;
    if (RESERVED_FIELD_KEYS.indexOf(field.key) !== -1) continue;
    if (field.type === 'instruction') continue;
    out[field.key] = fieldDefault(field);
  }
  return out;
}

/** The pack fields that render as attribute controls (reserved keys removed). */
export function attributeFields(fields: DomainField[]): DomainField[] {
  return (fields || []).filter((f) => f && f.key && RESERVED_FIELD_KEYS.indexOf(f.key) === -1);
}
